import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const DISHES = [
    {
        name: "Jerk Chicken",
        kind: "Signature",
        description:
            "Pimento-wood marinade with scotch bonnet, charred over open flame and finished with rum-jerk reduction.",
        image:
            "https://static.prod-images.emergentagent.com/jobs/c2b7593f-ae8f-4892-a9ba-b424e7885b58/images/ac8d89d2beb6f6181caed66430f6c32867fb9c236253afcb2b1cb0f8c9971530.png",
        accent: "burgundy",
    },
    {
        name: "Braised Oxtail",
        kind: "House Classic",
        description:
            "Slow-braised two hours with butter beans, allspice and a deep, glossy gravy. Served with rice & peas.",
        image:
            "https://static.prod-images.emergentagent.com/jobs/c2b7593f-ae8f-4892-a9ba-b424e7885b58/images/65e8f1bdd5220259ec07cf76535e570471e919ce8134256b60128d254cf5d544.png",
        accent: "gold",
    },
    {
        name: "Curry Chicken",
        kind: "Traditional",
        description:
            "Authentic Jamaican curry chicken, slow-simmered with island spices and tender potatoes. Served with gungo peas & rice and steamed cabbage for a true Caribbean classic.",
        image:
            "https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/jqstmtoi_8297D13F-5615-4362-B284-285AC91D213A.PNG",
        accent: "burgundy",
    },
    {
        name: "Vegan Caribbean",
        kind: "Plant-Forward",
        description:
            "Choose from Caribbean Scrambled Tofu, Curry Chickpea Stew, Trini Jerk, BBQ Chickless, Black Eyed Peas Savory Stew, or Chickpea & Heart of Palm Cakes. Bold Caribbean flavors, completely plant-based.",
        image:
            "https://static.prod-images.emergentagent.com/jobs/c2b7593f-ae8f-4892-a9ba-b424e7885b58/images/bb2057e7db338c750b2b93cb1afe76d4f202726fe38b41fa33fe63ac126a855a.png",
        accent: "gold",
    },
    {
        name: "Island Seafood",
        kind: "Coastal",
        description:
            "Escovitch Snapper, Kingfish, Fried Fish, Jerk Shrimp, Curry Shrimp, Garlic Shrimp, and Coconut Shrimp. Fresh island seafood prepared with bold Caribbean flavor.",
        image:
            "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/7b2tx0qd_6AED80A2-E5AD-4A8F-A910-849ACAE56061%202.PNG",
        accent: "burgundy",
    },
];

export default function SignatureDishes() {
    return (
        <section
            id="signature-dishes"
            data-testid="signature-dishes-section"
            className="relative empire-section bg-charcoal-800"
            aria-label="Signature Dishes"
        >
            <div className="empire-container">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
                    <div className="max-w-2xl">
                        <p className="empire-eyebrow">The Empire Table</p>
                        <h2
                            data-testid="dishes-heading"
                            className="mt-5 empire-subhead text-cream"
                        >
                            Signature dishes,<br />
                            <span className="italic text-gold">island-deep flavor.</span>
                        </h2>
                    </div>
                    <p className="empire-body max-w-md md:text-right">
                        Five plates that anchor the menu, from open-flame jerk to
                        slow-braised oxtail and our coconut-rich vegan classics.
                    </p>
                </div>

                {/* Asymmetric grid */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-cream/10">
                    {DISHES.map((d, i) => {
                        // first two span 3 cols, last three span 2 cols on md+
                        const span =
                            i < 2
                                ? "md:col-span-3"
                                : "md:col-span-2";
                        return (
                            <motion.article
                                key={d.name}
                                initial={{ opacity: 0, y: 28 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{
                                    duration: 0.8,
                                    delay: i * 0.06,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                data-testid={`dish-card-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`group relative overflow-hidden bg-charcoal ${span} aspect-[4/5] md:aspect-[5/6]`}
                            >
                                <img
                                    src={d.image}
                                    alt={`${d.name} — ${d.description}`}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                                />
                                {/* Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/45 to-transparent" />
                                <div className="absolute inset-0 bg-charcoal/10 group-hover:bg-charcoal/0 transition-colors duration-500" />

                                {/* Label */}
                                <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
                                    <span
                                        className={`text-[10px] uppercase tracking-[0.28em] font-medium ${
                                            d.accent === "gold"
                                                ? "text-gold"
                                                : "text-cream/85"
                                        }`}
                                    >
                                        {d.kind}
                                    </span>
                                    <span className="inline-flex h-9 w-9 items-center justify-center border border-cream/30 text-cream/80 group-hover:border-gold group-hover:text-gold transition-colors">
                                        <ArrowUpRight size={14} />
                                    </span>
                                </div>

                                {/* Text */}
                                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                                    <h3 className="font-serif text-3xl md:text-4xl text-cream tracking-tight leading-tight">
                                        {d.name}
                                    </h3>
                                    <p className="mt-3 text-cream/70 text-sm md:text-[15px] font-light leading-relaxed max-w-md">
                                        {d.description}
                                    </p>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>

                <div className="mt-14 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <a
                        href="/menu"
                        data-testid="dishes-view-menu"
                        className="empire-btn-secondary"
                    >
                        View Full Menu
                    </a>
                    <p className="text-cream/55 text-[13px] font-light">
                        Daily specials and seasonal plates announced on Instagram,
                        @empirejamaicafusionrestaurant.
                    </p>
                </div>
            </div>
        </section>
    );
}
